import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl, errText, downloadFile } from "@/lib/api";
import { StatusBadge, scoreColor } from "@/components/StatusBadge";
import { toast } from "sonner";
import { ReportOptionsDialog } from "@/components/ReportOptionsDialog";
import { GroupMultiFilter, matchGroups, groupsQS, UNGROUPED } from "@/components/GroupMultiFilter";
import { Loader2, FileSpreadsheet, FileText, Download, Table2, TrendingUp, X, FileDown } from "lucide-react";
import { ProgressChart } from "@/components/ProgressChart";

const COMPONENTS = [
  { key: "kehadiran", label: "Kehadiran", pct: "5%", color: "bg-emerald-500" },
  { key: "partisipasi", label: "Partisipasi", pct: "15%", color: "bg-blue-500" },
  { key: "kuis", label: "Kuis", pct: "20%", color: "bg-orange-500" },
  { key: "individu", label: "Individu", pct: "30%", color: "bg-amber-500" },
  { key: "kelompok", label: "Kelompok", pct: "30%", color: "bg-cyan-500" },
];

export default function Assessment() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");
  const [progress, setProgress] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [reportDlg, setReportDlg] = useState(null);

  useEffect(() => {
    api.get("/assessments").then((r) => setData(r.data)).finally(() => setLoading(false));
    api.get("/groups").then((r) => setGroups(r.data)).catch(() => {});
  }, []);

  const groupQS = (sel = groupFilter) => (sel.length ? `?${groupsQS(sel)}` : "");
  const suffixOf = (sel) => (sel.length === 0 ? "" : sel.length > 1 ? `_${sel.length}_kelompok` : `_${(sel[0] === UNGROUPED ? "belum_dikelompokkan" : sel[0]).replace(/\s+/g, "_").toLowerCase()}`);
  const groupSuffix = suffixOf(groupFilter);
  const filtered = data.filter((a) => matchGroups(groupFilter, a.group));
  const groupNames = groups.map((g) => g.name);
  const countIn = (sel) => data.filter((a) => matchGroups(sel, a.group)).length;

  const openProgress = async (a) => {
    setProgress({ emp: a, points: null });
    setProgressLoading(true);
    try {
      const { data } = await api.get(`/assessments/${a.employee_id}/progress`);
      setProgress({ emp: data.employee, points: data.points });
    } catch (e) { toast.error(errText(e)); setProgress(null); } finally { setProgressLoading(false); }
  };

  const withSections = (path, sections) => `${path}${path.includes("?") ? "&" : "?"}sections=${sections}`;

  const runDownload = async (path, name, msg, sections) => {
    try {
      await downloadFile(withSections(path, sections), name);
      toast.success(msg);
    } catch (e) { toast.error(errText(e)); throw e; }
  };

  const downloadReport = (a) => setReportDlg({
    title: "Unduh Rapor PDF", subtitle: `${a.name} · ${a.employee_id}`,
    run: (sec) => runDownload(`/assessments/${a.employee_id}/report`, `rapor_${a.employee_id}.pdf`, `Rapor ${a.name} diunduh`, sec),
  });

  const downloadAllReports = () => setReportDlg({
    title: "Unduh Rapor Massal (PDF)", subtitle: "Pilih kelompok dan isi laporan", withGroups: true,
    run: (sec, g) => countIn(g) === 0 ? Promise.reject(toast.error("Tidak ada karyawan pada kelompok terpilih"))
      : runDownload(`/assessments/report/all${groupQS(g)}`, `rapor_semua_karyawan${suffixOf(g)}.pdf`, `Rapor massal ${countIn(g)} karyawan diunduh`, sec),
  });

  const downloadExcel = () => setReportDlg({
    title: "Ekspor Excel Gradebook", subtitle: "Pilih kelompok dan isi laporan", withGroups: true,
    run: (sec, g) => countIn(g) === 0 ? Promise.reject(toast.error("Tidak ada karyawan pada kelompok terpilih"))
      : runDownload(`/assessments/export/excel${groupQS(g)}`, `rekap_penilaian${suffixOf(g)}.xlsx`, `Rekap Excel ${countIn(g)} karyawan diunduh`, sec),
  });

  const doExport = async (kind) => {
    setExporting(kind);
    try {
      if (kind === "excel") await downloadFile(`/assessments/export/excel${groupQS()}`, `rekap_penilaian${groupSuffix}.xlsx`);
      else await downloadFile(`/assessments/export/pdf${groupQS()}`, `rekap_penilaian${groupSuffix}.pdf`);
      toast.success(`Rekap ${kind === "excel" ? "Excel" : "PDF"} berhasil diunduh`);
    } catch (e) { toast.error(errText(e)); } finally { setExporting(""); }
  };

  const downloadCert = async (a) => {
    try {
      await downloadFile(`/assessments/${a.employee_id}/certificate`, `sertifikat_${a.employee_id}.pdf`);
      toast.success(`Sertifikat ${a.name} diunduh`);
    } catch (e) { toast.error(errText(e)); }
  };

  if (loading) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">Rekap Penilaian</h1>
          <p className="mt-1 text-slate-500">Nilai komponen terbobot dari seluruh tugas. Batas kelulusan ≥ 7.0.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <GroupMultiFilter testid="rekap-group-filter" names={groupNames} value={groupFilter} onChange={setGroupFilter} />
          <button data-testid="open-gradebook-button" onClick={() => navigate("/gradebook")} className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition">
            <Table2 className="h-4 w-4" /> Buka Gradebook
          </button>
          <button data-testid="export-rapor-all-button" onClick={downloadAllReports} disabled={exporting} className="flex items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 transition disabled:opacity-60">
            {exporting === "rapor" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Rapor Massal
          </button>
          <button data-testid="export-excel-button" onClick={downloadExcel} disabled={exporting} className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 transition disabled:opacity-60">
            {exporting === "excel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Excel
          </button>
          <button data-testid="export-pdf-button" onClick={() => doExport("pdf")} disabled={exporting} className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-700 hover:bg-rose-100 transition disabled:opacity-60">
            {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} PDF
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {COMPONENTS.map((w) => (
          <span key={w.key} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
            <span className={`h-2 w-2 rounded-full ${w.color}`} /> {w.label} {w.pct}
          </span>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100 bg-slate-50/50">
                <th className="px-5 py-3 font-semibold">Karyawan</th>
                {COMPONENTS.map((c) => <th key={c.key} className="px-3 py-3 font-semibold text-center">{c.label}</th>)}
                <th className="px-3 py-3 font-semibold text-center">Nilai Akhir</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.employee_id} data-testid={`assessment-row-${a.employee_id}`} className="border-b border-slate-50 hover:bg-slate-50 transition">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {a.photo_url ? <img src={fileUrl(a.photo_url)} alt="" className="h-9 w-9 rounded-full object-cover" /> : <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-500">{a.name[0]}</div>}
                      <div>
                        <p className="font-semibold text-slate-800">{a.name}</p>
                        <p className="text-xs text-slate-400 font-mono">{a.employee_id}{a.group ? ` · ${a.group}` : ""}</p>
                      </div>
                    </div>
                  </td>
                  {COMPONENTS.map((c) => {
                    const isActive = (a.active_components || []).includes(c.key);
                    return (
                    <td key={c.key} className="px-3 py-3 text-center">
                      {isActive ? (
                        <>
                          <span className="font-semibold text-slate-700">{a[c.key]}</span>
                          <span className="block text-[10px] text-slate-400">{a.completeness[c.key].graded}/{a.completeness[c.key].total} dinilai</span>
                        </>
                      ) : (
                        <span className="text-[10px] font-medium italic text-slate-300" data-testid={`rekap-inactive-${a.employee_id}-${c.key}`}>tidak dinilai</span>
                      )}
                    </td>
                    );
                  })}
                  <td className={`px-3 py-3 text-center font-heading text-lg font-extrabold ${scoreColor(a.final_score)}`}>{a.final_score}</td>
                  <td className="px-5 py-3"><StatusBadge status={a.status} /></td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button data-testid={`progress-${a.employee_id}`} onClick={() => openProgress(a)} title="Grafik progres" className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-100">
                        <TrendingUp className="h-3.5 w-3.5" /> Progres
                      </button>
                      <button data-testid={`report-${a.employee_id}`} onClick={() => downloadReport(a)} title="Unduh rapor PDF" className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200">
                        <FileDown className="h-3.5 w-3.5" /> Rapor
                      </button>
                      {a.status === "Direkomendasikan" && (
                        <button data-testid={`certificate-${a.employee_id}`} onClick={() => downloadCert(a)} title="Unduh sertifikat" className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-600 hover:bg-emerald-100">
                          <Download className="h-3.5 w-3.5" /> Sertifikat
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {reportDlg && (
        <ReportOptionsDialog title={reportDlg.title} subtitle={reportDlg.subtitle} onClose={() => setReportDlg(null)}
          onDownload={reportDlg.run}
          groupNames={reportDlg.withGroups ? groupNames : undefined} initialGroups={groupFilter} />
      )}

      {progress && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setProgress(null)} />
          <div className="relative w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
            <button onClick={() => setProgress(null)} className="absolute right-4 top-4"><X className="h-5 w-5 text-slate-400" /></button>
            <div className="flex items-center gap-3 mb-1">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              <h3 className="font-heading text-xl font-bold text-slate-900">Grafik Progres Nilai</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">{progress.emp.name} · {progress.emp.employee_id}</p>
            {progressLoading || !progress.points ? (
              <div className="flex h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>
            ) : (
              <ProgressChart points={progress.points} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
